import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { ContractLandXrefs, LeaseXrefs, WellXrefs } from '../types/api';

/**
 * The three Land Data entities that can cross-reference each other (spec
 * docs/superpowers/specs/2026-08-16-land-data-xrefs-design.md §4). Also used
 * as the `type` value in POST/DELETE bodies — a "type" is just the OTHER
 * entity kind being linked to.
 */
export type XrefEntity = 'lease' | 'contract' | 'well';

const ENTITY_BASE_PATH: Record<XrefEntity, string> = {
  lease: '/leases',
  contract: '/contracts-land',
  well: '/wells',
};

/**
 * Each base entity links to exactly the other two (api/src/routes/land-xrefs.ts's
 * relation config) — never to its own kind.
 */
export const XREF_RELATED_TYPES: Record<XrefEntity, XrefEntity[]> = {
  lease: ['contract', 'well'],
  contract: ['lease', 'well'],
  well: ['lease', 'contract'],
};

export type XrefsResponseByEntity = {
  lease: LeaseXrefs;
  contract: ContractLandXrefs;
  well: WellXrefs;
};

export const xrefsKeys = {
  all: ['xrefs'] as const,
  detail: (entity: XrefEntity, projectId: string | null, id: string) =>
    [...xrefsKeys.all, entity, projectId, id] as const,
};

export function useXrefs<E extends XrefEntity>(entity: E, id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? xrefsKeys.detail(entity, projectId, id) : [...xrefsKeys.all, entity, 'none'],
    enabled: id != null && !!projectId,
    queryFn: async () => {
      const res = await api.get<XrefsResponseByEntity[E]>(
        `${ENTITY_BASE_PATH[entity]}/${id}/xrefs`,
      );
      return res;
    },
  });
}

export interface XrefMutationInput {
  type: XrefEntity;
  target_id: string;
}

interface XrefLinkResponse {
  xref: { id: string; project_id: string; deleted_at: string | null; [k: string]: unknown };
}

/**
 * Links `id` (of `entity`) to `target_id` (of `type`). On success, invalidates
 * BOTH this entity's xrefs query and the related entity's xrefs query, so a
 * link added from one drawer shows up immediately if the other entity's
 * drawer is open too (spec §4: "bidirectional by construction").
 */
export function useAddXref(entity: XrefEntity, id: string | null) {
  const qc = useQueryClient();
  const projectId = useProjectStore((s) => s.projectId);
  return useMutation({
    mutationFn: (input: XrefMutationInput) => {
      if (id == null) {
        return Promise.reject(
          new Error('Cannot add a cross-reference before the record is saved.'),
        );
      }
      return api.post<XrefLinkResponse>(`${ENTITY_BASE_PATH[entity]}/${id}/xrefs`, input);
    },
    onSuccess: (_data, variables) => {
      if (id == null) return;
      qc.invalidateQueries({ queryKey: xrefsKeys.detail(entity, projectId, id) });
      qc.invalidateQueries({
        queryKey: xrefsKeys.detail(variables.type, projectId, variables.target_id),
      });
    },
  });
}

/** Unlinks `id` (of `entity`) from `target_id` (of `type`); same bidirectional
 * invalidation as {@link useAddXref}. */
export function useRemoveXref(entity: XrefEntity, id: string | null) {
  const qc = useQueryClient();
  const projectId = useProjectStore((s) => s.projectId);
  return useMutation({
    mutationFn: ({ type, target_id }: XrefMutationInput) => {
      if (id == null) {
        return Promise.reject(
          new Error('Cannot remove a cross-reference before the record is saved.'),
        );
      }
      return api.delete(`${ENTITY_BASE_PATH[entity]}/${id}/xrefs/${type}/${target_id}`);
    },
    onSuccess: (_data, variables) => {
      if (id == null) return;
      qc.invalidateQueries({ queryKey: xrefsKeys.detail(entity, projectId, id) });
      qc.invalidateQueries({
        queryKey: xrefsKeys.detail(variables.type, projectId, variables.target_id),
      });
    },
  });
}
